/**
 * 运行时注入的外部配置。
 *
 * 为什么需要它：几个 n8n webhook 地址原先硬编码在调用点（如 `translateWithAI`），
 * 换环境或换租户就得改代码重新发版。
 * 现在改为由外层客户端在启动时注入。
 *
 * 注入通道复用现有机制，不新增协议：dmex 通过
 * `webview.executeJavaScript('window.setAIConfig(...)')` 下发 AI 配置
 * （见 dmex 侧 App.vue 的 handleGetConfig），tweb 的 `setAIConfig` 把其中的
 * `webhooks` 字段转到这里。
 *
 * 两条设计约束：
 *
 * 1. **取值发生在调用时，不在模块加载时快照**。注入是异步到达的
 *    （`getAIConfig()` 走 `yaychat://get_config`），若在模块顶层把值存进常量，
 *    就永远只会拿到默认值，而且不报错——是那种"改了没生效"的难查问题。
 *
 * 2. **没注入时必须与改造前完全一致**。`DEFAULTS` 保留原先硬编码的地址，
 *    注入缺失或为空时逐项回退，绝不把请求发到空地址。
 */

export type WebhookKey = 'translate'

/**
 * 改造前硬编码在各调用点的地址，作为未注入时的回退。
 *
 * 目前只剩翻译一条：AI 应答已改走服务端工作流接口
 * （`/api/v1/client/workflows/invoke`），回复由长连接推送，不再需要 webhook 轮询。
 */
const DEFAULTS: Record<WebhookKey, string> = {
  /**
   * 翻译已改为**优先走自有接口** `GET /v1/translate/tr`（见 translate_util.ts），
   * 这条 webhook 降级为接口不可用时的回退。保留注入能力是为了让回退地址也能换，
   * 而不用改代码重新发版。
   */
  translate: 'https://n8n.yaycha.com/webhook/3017f387-4853-42ca-864b-52ca1c4da4db'
}

const ALL_KEYS = Object.keys(DEFAULTS) as WebhookKey[]

const injected: Partial<Record<WebhookKey, string>> = {}

function normalize(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 注入一组 webhook 地址。
 *
 * 空值/非字符串一律忽略（保留默认值或上一次注入的值）：一遍遍地用"没给"覆盖掉
 * 已有配置，会让已经注入成功的地址莫名回退到默认。
 */
export function setWebhookConfig(patch: unknown): void {
  if (!patch || typeof patch !== 'object') return

  const source = patch as Record<string, unknown>
  const applied: WebhookKey[] = []
  ALL_KEYS.forEach((key) => {
    const value = normalize(source[key])
    if (!value || value === injected[key]) return
    injected[key] = value
    applied.push(key)
  })

  // 只在真的变化时打一条，便于排查"注入有没有到"；地址本身不是密钥
  if (applied.length) {
    console.log(`** runtimeConfig: webhook 已注入 [${applied.join(', ')}]`)
  }
}

/** 取某个 webhook 地址；未注入时回退到默认值 */
export function getWebhookUrl(key: WebhookKey): string {
  return injected[key] || DEFAULTS[key]
}

/** 当前每一项的来源，供排查用（不打印地址本身） */
export function getWebhookConfigSource(): Record<WebhookKey, 'injected' | 'default'> {
  const result = {} as Record<WebhookKey, 'injected' | 'default'>
  ALL_KEYS.forEach((key) => {
    result[key] = injected[key] ? 'injected' : 'default'
  })
  return result
}
